import { notification } from "@timeedit/ui-components/antd";
import { MessageState, NotificationState } from "./types";
import { useMessage } from "./useMessage";
import { useNotification } from "./useNotification";
import { translations } from "@timeedit/registration-shared";

/**
 * The notice state every app keeps in its store under `notice`. An app with
 * notices of its own extends it rather than redefining these.
 */
export interface NoticeState {
  notification: NotificationState;
  message: MessageState;
}

export const initialNoticeState: NoticeState = {
  notification: {
    type: "error",
    title: "",
    description: undefined,
  },
  message: {
    type: "info",
    content: "",
    duration: undefined,
  },
};

type Action<T> = { type: string; payload: T };

/**
 * Case reducers for an app's notice slice, kept free of redux so that this
 * package does not depend on it.
 *
 * @example
 * createSlice({ name: "notice", initialState: initialNoticeState, reducers: noticeReducers });
 */
export const noticeReducers = {
  /** A notification, which stays until closed. For anything with a title. */
  setNotification: (
    state: NoticeState,
    action: Action<NotificationState>
  ): NoticeState => ({ ...state, notification: action.payload }),
  /** A short message, which closes by itself. */
  setMessage: (
    state: NoticeState,
    action: Action<MessageState>
  ): NoticeState => ({ ...state, message: action.payload }),
};

type UseNoticesProps = NoticeState & {
  copy?: string;
};

/**
 * Shows the notices in the given state. Render what it returns once, near
 * the root, so that the notices pick up the app's theme.
 */
export function useNotices({
  notification: notificationState,
  message,
  copy = translations.copy,
}: UseNoticesProps): React.JSX.Element {
  const [notificationApi, notificationContext] = notification.useNotification();
  useNotification({ notificationApi, notificationState, copy });
  const messageContext = useMessage(message);

  return (
    <>
      {notificationContext}
      {messageContext}
    </>
  );
}
